/* eslint-disable prettier/prettier */
import React, { useEffect, useState } from 'react';

// material-ui
import { Grid, Typography, Table, TableBody, TableCell, TableRow, TableContainer, TableHead, Chip, Button, Tooltip } from '@mui/material';

// project imports
import PlayerCell from 'views/components/playerCell.component';

// assets
import useLeagues from 'hooks/useLeagues';
import ArrowDropDownCircleIcon from '@mui/icons-material/ArrowDropDownCircle';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import getPointDifferentialPercentage from 'utils/pointDifferentialPercentage';

const LeaderboardTable = ({ eventName, leagueId, showAdmin = false, hasLeaderboard, week, leagueLeaderboard }) => {
    const { getLeaguePlayers } = useLeagues();

    const initialTableRows = [
        { tooltip: 'Games Won', text: 'W' },
        { tooltip: 'Games Lost', text: 'L' },
        { tooltip: 'Points Earned', text: 'PE' },
        { tooltip: 'Points Against', text: 'PA' },
        { tooltip: 'Point Differential', text: 'PD' },
        { tooltip: 'Point Differential %', text: 'PD %' },
        { tooltip: 'Position Change from Start', text: 'Position Change' }
    ];

    const getPositionChangeIcon = (positionChange) => {
        if (positionChange < 0) {
            return <ArrowDropDownCircleIcon color="error" />;
        }
        if (positionChange === 0) {
            return <PlayCircleFilledIcon color="warning" />;
        }
        if (positionChange > 0) {
            return <ArrowDropDownCircleIcon color="success" sx={{ transform: 'rotate(180deg)' }} />;
        }
        return <ArrowDropDownCircleIcon />;
    };

    useEffect(() => {
        if (!hasLeaderboard) {
            getLeaguePlayers(leagueId, null);
        }
    }, [leagueId, hasLeaderboard]);

    function renderAdmin() {
        return (
            <Grid
                container
                alignItems="center"
                justifyContent="space-between"
                mt={2}
                sx={{
                    backgroundColor: 'white',
                    borderRadius: 1,
                    paddingX: 2,
                    paddingY: 2,
                    borderBottom: '1px solid lightgrey'
                }}
            >
                <Grid item>
                    <Grid container alignItems="center">
                        <Grid item>
                            <Typography
                                variant="h4"
                                sx={{
                                    color: 'primary.text'
                                }}
                            >
                                {eventName}
                            </Typography>
                        </Grid>
                        <Grid item ml={2}>
                            <Chip label="No Matchups" color="error" variant="filled" />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        );
    }
    return (
        <Grid>
            {showAdmin && renderAdmin()}
            <TableContainer>
                <Table size="small" aria-label={eventName} sx={{ backgroundColor: 'white', borderRadius: 2 }}>
                    <TableHead>
                        {hasLeaderboard && (
                            <TableRow>
                                <TableCell colSpan={2} />
                                <TableCell colSpan={6} align="center" sx={{ backgroundColor: 'gainsboro' }}>
                                    <Typography variant="h4">Week {week} Results</Typography>
                                </TableCell>
                                <TableCell colSpan={1} align="center">
                                    <Typography variant="h4">All-season</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                        <TableRow>
                            <TableCell align="center" width="5%">
                                {hasLeaderboard ? 'Rank' : 'Seed'}
                            </TableCell>
                            <TableCell>Players</TableCell>
                            {hasLeaderboard && (
                                <>
                                    {initialTableRows.map((row) => (
                                        <TableCell
                                            align={row.text === 'Position Change' ? 'start' : 'center'}
                                            sx={{ backgroundColor: row.text === 'Position Change' ? 'white' : 'gainsboro' }}
                                            key={row?.id}
                                        >
                                            <Tooltip title={row.tooltip}>
                                                <Typography variant="h5"> {row.text} </Typography>
                                            </Tooltip>
                                        </TableCell>
                                    ))}
                                    {showAdmin && (
                                        <TableCell>
                                            <Tooltip title="Amount earned in Micro-Payments">
                                                <Typography variant="h5"> Venmo ID </Typography>
                                            </Tooltip>
                                        </TableCell>
                                    )}
                                </>
                            )}
                        </TableRow>
                    </TableHead>
                    {leagueLeaderboard?.map((team, teamIndex) => (
                        <TableBody>
                            <TableRow>
                                <TableCell align="center" sx={{ p: 0 }}>
                                    <Typography variant="subtitle1">{teamIndex + 1}</Typography>
                                </TableCell>
                                <TableCell
                                    sx={{
                                        flexDirection: 'column',
                                        minWidth: '150px',
                                        whiteSpace: 'nowrap',
                                        p: 0.5,
                                        pl: 1.5
                                    }}
                                >
                                    <Grid
                                        container
                                        sx={{
                                            flexDirection: 'column',
                                            whiteSpace: 'nowrap',
                                            minWidth: '150px'
                                        }}
                                    >
                                        <PlayerCell user={team?.user} />
                                    </Grid>
                                </TableCell>
                                {hasLeaderboard && (
                                    <>
                                        <TableCell align="center" sx={{ backgroundColor: 'gainsboro', p: 0 }}>
                                            {team.wins}
                                        </TableCell>
                                        <TableCell align="center" sx={{ backgroundColor: 'gainsboro', p: 0 }}>
                                            {team.losses}
                                        </TableCell>
                                        <TableCell align="center" sx={{ backgroundColor: 'gainsboro', p: 0 }}>
                                            {team.points_earned}
                                        </TableCell>
                                        <TableCell align="center" sx={{ backgroundColor: 'gainsboro', p: 0 }}>
                                            {team.points_against}
                                        </TableCell>
                                        <TableCell align="center" sx={{ backgroundColor: 'gainsboro', p: 0 }}>
                                            {team.point_differential}
                                        </TableCell>
                                        <TableCell align="center" sx={{ backgroundColor: 'gainsboro', p: 0 }}>
                                            {getPointDifferentialPercentage(team.points_earned, team.points_against)}
                                        </TableCell>
                                        <TableCell align="center" sx={{ p: 0, pl: 1 }}>
                                            <Grid container ml={1} alignItems="center">
                                                {getPositionChangeIcon(team?.position_change || 0)}
                                                <Typography ml={1}>{team?.position_change || 0} </Typography>
                                            </Grid>
                                        </TableCell>
                                        {showAdmin && <TableCell align="left">{team.user?.user_attributes?.venmo_id}</TableCell>}
                                    </>
                                )}
                            </TableRow>
                        </TableBody>
                    ))}
                </Table>
            </TableContainer>
        </Grid>
    );
};

export default LeaderboardTable;
